import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Typography
} from '@mui/material';
import {
  Search
} from '@mui/icons-material';
import { COLORS } from '../../shared/colors';
import { FILTER_VALUES } from '../../shared/appConstants';
import { useTranslation } from 'react-i18next';
import OverflowTooltip from '../../shared/OverflowTooltip';

const EpicSelector = ({ 
  value, 
  onChange, 
  projectId,
  searchTickets,
  onEpicDateChange, // New prop to handle date changes
  loadInitialEpics = false // New prop to trigger initial load
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedEpicData, setSelectedEpicData] = useState(null); // Store selected epic data
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const searchInputRef = useRef(null);
  const epicCacheRef = useRef(new Map());
  const [selectedFallback, setSelectedFallback] = useState(null);

  // Debounced search function
  const debouncedSearch = useCallback(
    async (term) => {
      if (!projectId || projectId === FILTER_VALUES.ALL) {
        setSearchResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const results = await searchTickets({
          projectId,
          searchTerm: term.trim(),
          task_type: 'Epic'
        });
        // Handle API response structure - extract data array if it exists
        const epicsData = results?.data || results || [];
        setSearchResults(epicsData);
        epicsData.forEach((epic) => {
          epicCacheRef.current.set(String(epic.id), epic);
        });
      } catch (error) {
        console.error('Epic search failed:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    },
    [projectId, searchTickets]
  );

  // Debounce search input - search when user types or load initial when empty
  useEffect(() => {
    if (searchTerm.trim()) {
      const timeoutId = setTimeout(() => {
        debouncedSearch(searchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    } else if (loadInitialEpics && projectId && projectId !== FILTER_VALUES.ALL) {
      // Load initial epics when search term is empty but loadInitialEpics is true
      const timeoutId = setTimeout(() => {
        debouncedSearch('');
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // Don't clear results when search term is empty if loadInitialEpics is true
  }, [searchTerm, debouncedSearch, loadInitialEpics, projectId]);

  // Clear search when closed, but preserve results when selecting
  useEffect(() => {
    if (!open) {
      // Only clear search term and results when dropdown is closed
      // Add a small delay to allow for selection to complete
      const timeoutId = setTimeout(() => {
        setSearchTerm('');
        // Don't clear search results immediately - keep them for next open
        // setSearchResults([]);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    const focusId = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => clearTimeout(focusId);
  }, [open]);

  const handleChange = (event) => {
    const newValue = event.target.value;
    const newValueKey = String(newValue);
    
    // Find the selected epic to get its data and date range
    if (newValue && newValue !== FILTER_VALUES.ALL) {
      const selectedEpic = searchResults.find(epic => String(epic.id) === newValueKey);
      if (selectedEpic) {
        setSelectedEpicData(selectedEpic); // Store the selected epic data
        epicCacheRef.current.set(String(selectedEpic.id), selectedEpic);
        const displayText = selectedEpic.ticket_number
          ? `${selectedEpic.ticket_number} ${selectedEpic.title}`
          : selectedEpic.title;
        setSelectedFallback({ id: String(selectedEpic.id), label: displayText });
        if (onEpicDateChange) {
          const epicData = {
            startDate: selectedEpic.start_date,
            dueDate: selectedEpic.due_date,
            epicStartDate: selectedEpic.start_date // Pass epic start date for Gantt calculations
          };
          onEpicDateChange(epicData);
        }
      }
    } else {
      setSelectedEpicData(null); // Clear when "All Epics" is selected
      setSelectedFallback(null);
      if (onEpicDateChange) {
        onEpicDateChange({
          startDate: null,
          dueDate: null,
          epicStartDate: null
        });
      }
    }
    
    onChange(newValue);
    
    // Keep search results visible after selection
    // Don't clear search results immediately
  };

  useEffect(() => {
    if (!value || value === FILTER_VALUES.ALL) return;
    const valueKey = String(value);
    if (selectedEpicData?.id && String(selectedEpicData.id) === valueKey) return;
    const cached = epicCacheRef.current.get(valueKey);
    if (cached) {
      setSelectedEpicData(cached);
      const displayText = cached.ticket_number
        ? `${cached.ticket_number} ${cached.title}`
        : cached.title;
      setSelectedFallback({ id: String(cached.id), label: displayText });
    }
  }, [value, selectedEpicData]);

  const renderValue = (selected) => {
    if (!selected || selected === FILTER_VALUES.ALL) {
      return t('dashboard.allEpics');
    }
    
    // Use stored epic data or try to find in search results
    const selectedKey = String(selected);
    let epic = selectedEpicData && String(selectedEpicData.id) === selectedKey
      ? selectedEpicData
      : null;
    if (!epic) {
      epic = searchResults.find(e => String(e.id) === selectedKey);
    }
    if (!epic) {
      epic = epicCacheRef.current.get(selectedKey) || null;
    }
    
    const displayText = epic
      ? (epic.ticket_number ? `${epic.ticket_number} ${epic.title}` : epic.title)
      : selectedFallback?.id === selectedKey
        ? selectedFallback.label
        : null;

    if (displayText) {
      return (
        <OverflowTooltip
          title={displayText}
          tooltipWidth={300}
          placement="top-start"
          component={Box}
          className="filter-selected-value-text"
        />
      );
    }

    // Show loading placeholder while data loads
    return <CircularProgress size={12} sx={{ color: COLORS.gray[400] }} />;
  };

  return (
    <FormControl size="small" className="filter-header-select epic-selector" fullWidth>
      <Select
        multiple={false}
        value={value || FILTER_VALUES.ALL}
        onChange={handleChange}
        open={open}
        onOpen={() => {
          setOpen(true);
          // Load epics when dropdown opens if no search results and loadInitialEpics is true
          if (loadInitialEpics && searchResults.length === 0 && projectId && projectId !== FILTER_VALUES.ALL) {
            debouncedSearch('');
          }
          setTimeout(() => {
            searchInputRef.current?.focus();
          }, 0);
        }}
        onClose={() => setOpen(false)}
        renderValue={renderValue}
        displayEmpty
        MenuProps={{
          disableAutoFocusItem: true,
          MenuListProps: {
            autoFocusItem: false
          },
          PaperProps: {
            style: {
              maxHeight: 300,
              overflow: 'auto'
            }
          }
        }}
        sx={{
          fontSize: '14px',
          '& .MuiSelect-select': {
            minHeight: 'auto',
            paddingTop: '4px !important',
            paddingBottom: '4px !important',
          }
        }}
      >
        {/* Search input */}
        <MenuItem disableRipple sx={{ p: 1 }}>
          <TextField
            size="small"
            placeholder={t('dashboard.searchEpics')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            inputRef={searchInputRef}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 20, color: COLORS.gray[400] }} />
                </InputAdornment>
              ),
              endAdornment: loading && (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              )
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </MenuItem>

        {/* All Epics option */}
        <MenuItem value={FILTER_VALUES.ALL}>
          {t('dashboard.allEpics')}
        </MenuItem>

        {/* Search results */}
        {searchResults.length > 0 ? (
          searchResults.map((epic) => {
            const displayText = epic.ticket_number ? `${epic.ticket_number} ${epic.title}` : epic.title;
            return (
              <MenuItem key={epic.id} value={epic.id} sx={{ maxWidth: 280 }}>
                <OverflowTooltip
                  title={displayText}
                  tooltipWidth={300}
                  placement="top-start"
                  component={Typography}
                  variant="body2"
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  {displayText}
                </OverflowTooltip>
              </MenuItem>
            );
          })
        ) : searchTerm && !loading ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.noEpicsFound')}
            </Typography>
          </MenuItem>
        ) : !searchTerm && !loading ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.startTypingToSearchEpics')}
            </Typography>
          </MenuItem>
        ) : null}
      </Select>
    </FormControl>
  );
};

export default EpicSelector;
